"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import api from "../../services/api";

const PaymentForm = ({ user, onSaveSuccess, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    reset,
    watch,
  } = useForm();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const selectedCourseId = watch("courseId");
  const selectedCourse = courses.find((c) => c._id === selectedCourseId);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [studentsRes, coursesRes] = await Promise.all([
          api.get("/users?role=student"),
          api.get("/courses"),
        ]);

        // Ensure only students are added
        const filteredStudents = (studentsRes.data.data || []).filter(
          (user) => {
            if (typeof user.role === "object" && user.role !== null) {
              return user.role.name === "student";
            }
            return user.role === "student";
          }
        );

        // Filter active courses as fallback
        const activeCourses =
          coursesRes.data.data?.filter((c) => c.status === "active") || [];

        setStudents(filteredStudents);
        setCourses(activeCourses);
      } catch (error) {
        toast.error("Failed to load required data");
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const onSubmit = async (data) => {
    try {
      const payload = {
        studentId: data.studentId,
        courseId: data.courseId,
        amount: parseFloat(data.amount),
        paymentMethod: data.paymentMethod,
      };

      if (isNaN(payload.amount) || payload.amount <= 0) {
        throw new Error("Invalid payment amount");
      }

      const response = await api.post("/payments", payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Payment failed");
      }

      toast.success("Payment recorded successfully");
      reset();
      onSaveSuccess?.();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Payment processing failed"
      );
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="inline-block w-6 h-6 border-2 border-current rounded-full animate-spin border-t-transparent" />
        <p className="mt-2">Loading form data...</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="max-w-2xl mx-auto space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Student Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Student <span className="text-red-500">*</span>
          </label>
          <select
            {...register("studentId", {
              required: "Student selection is required",
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm ${
              errors.studentId
                ? "border-red-500"
                : "focus:ring-blue-500 focus:border-blue-500"
            }`}
            disabled={isSubmitting}
          >
            <option value="">Select Student</option>
            {students.map((student) => (
              <option key={student._id} value={student._id}>
                {student.firstName} {student.lastName}
              </option>
            ))}
          </select>
          {errors.studentId && (
            <p className="mt-1 text-sm text-red-500">
              {errors.studentId.message}
            </p>
          )}
        </div>

        {/* Course Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Course <span className="text-red-500">*</span>
          </label>
          <select
            {...register("courseId", {
              required: "Course selection is required",
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm ${
              errors.courseId
                ? "border-red-500"
                : "focus:ring-blue-500 focus:border-blue-500"
            }`}
            disabled={isSubmitting}
          >
            <option value="">Select Course</option>
            {courses.map((course) => (
              <option key={course._id} value={course._id}>
                {course.name} (Price: ${course.price || 0})
              </option>
            ))}
          </select>
          {errors.courseId && (
            <p className="mt-1 text-sm text-red-500">
              {errors.courseId.message}
            </p>
          )}
        </div>

        {/* Amount Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Amount <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
              $
            </span>
            <input
              type="number"
              step="0.01"
              {...register("amount", {
                required: "Amount is required",
                min: {
                  value: 0.01,
                  message: "Amount must be at least $0.01",
                },
                validate: (value) => parseFloat(value) > 0 || "Invalid amount",
              })}
              className={`w-full pl-7 pr-3 py-2 border rounded-md shadow-sm ${
                errors.amount
                  ? "border-red-500"
                  : "focus:ring-blue-500 focus:border-blue-500"
              }`}
              disabled={isSubmitting}
              placeholder={
                selectedCourse?.price
                  ? `Suggested: $${selectedCourse.price}`
                  : ""
              }
            />
          </div>
          {errors.amount && (
            <p className="mt-1 text-sm text-red-500">{errors.amount.message}</p>
          )}
        </div>

        {/* Payment Method */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Payment Method <span className="text-red-500">*</span>
          </label>
          <select
            {...register("paymentMethod", {
              required: "Payment method is required",
            })}
            className={`w-full px-3 py-2 border rounded-md shadow-sm ${
              errors.paymentMethod
                ? "border-red-500"
                : "focus:ring-blue-500 focus:border-blue-500"
            }`}
            disabled={isSubmitting}
          >
            <option value="Cash">Cash</option>
            <option value="Credit Card">Credit Card</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="Online">Online Payment</option>
          </select>
          {errors.paymentMethod && (
            <p className="mt-1 text-sm text-red-500">
              {errors.paymentMethod.message}
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="animate-pulse">Processing...</span>
            </>
          ) : (
            "Record Payment"
          )}
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
